// Media processing plan (PLAN §3 provider policy, §5 step 2, task 12a): which service prepares a file for OCR or
// transcription. Native first: the Images binding for HEIC photos, Media Transformations for short videos; the
// media Container (ffmpeg, poppler, ImageMagick) only where those have limits. Pure logic, unit-tested here and
// executed by the pipeline's OCR stage.

import { extensionOf, formatOf } from './uploads';

const MB = 1024 * 1024;

/** Images binding: largest input accepted by `.input()`. */
export const IMAGES_MAX_BYTES = 20 * MB;
/** Media Transformations: largest input, longest input, longest output per call. */
export const MEDIA_TRANSFORM_MAX_BYTES = 100 * MB;
export const MEDIA_TRANSFORM_MAX_S = 10 * 60;
export const MEDIA_TRANSFORM_SLICE_S = 60;
/** Container sound segments for long audio and video (PLAN §5: ~10-minute segments). */
export const AUDIO_SEGMENT_S = 10 * 60;
/** Scanned-PDF pages are rasterized at this resolution for OCR. */
export const SCAN_DPI = 200;

/** Photo formats a vision model reads as they are. */
const DIRECT_IMAGE = new Set(['jpg', 'jpeg', 'png', 'webp']);
/** Sound formats speech recognition reads as they are. */
const DIRECT_AUDIO = new Set(['mp3', 'wav', 'm4a', 'ogg']);
/** Video containers Media Transformations is built for (MP4 / H.264); other videos go to the Container. */
const TRANSFORMABLE_VIDEO = new Set(['mp4']);

export type MediaOutput = 'page_image' | 'audio';

export type MediaPlan =
  /** Nothing to prepare: a text document, or a scan of a format that can't be rasterized. */
  | { kind: 'none' }
  /** The original goes to OCR / transcription as it is. */
  | { kind: 'original'; output: MediaOutput }
  /** HEIC photo → JPEG through the Images binding. */
  | { kind: 'images' }
  /** Short video → M4A sound, one Media Transformations call per slice (each at most a minute). */
  | { kind: 'media'; slices: { startS: number; durationS: number }[] }
  /** The media Container: PDF pages, image pages (TIFF, large HEIC) or sound segments. */
  | { kind: 'container'; op: 'pdf' | 'image' | 'audio'; format?: 'heic' | 'tiff' };

export interface MediaFile {
  filename: string;
  size: number;
  /** Duration read from the file's headers while reading (seconds, may be fractional); null when unknown. */
  durationS: number | null;
  /** Reading found no text layer. */
  scan: boolean;
}

/** One-minute slices covering a short video, the last one only as long as what's left. */
export function mediaSlices(durationS: number): { startS: number; durationS: number }[] {
  const out: { startS: number; durationS: number }[] = [];
  for (let t = 0; t < durationS; t += MEDIA_TRANSFORM_SLICE_S)
    out.push({ startS: t, durationS: Math.max(1, Math.min(MEDIA_TRANSFORM_SLICE_S, Math.ceil(durationS - t))) });
  return out;
}

export function planMedia(file: MediaFile): MediaPlan {
  const ext = extensionOf(file.filename);
  const family = formatOf(file.filename)?.family;
  const d = file.durationS;
  const short = typeof d === 'number' && d > 0 && d <= MEDIA_TRANSFORM_MAX_S;

  if (family === 'image') {
    if (ext === 'heic') return file.size <= IMAGES_MAX_BYTES ? { kind: 'images' } : { kind: 'container', op: 'image', format: 'heic' };
    if (ext === 'tif' || ext === 'tiff') return { kind: 'container', op: 'image', format: 'tiff' };
    return DIRECT_IMAGE.has(ext) ? { kind: 'original', output: 'page_image' } : { kind: 'none' };
  }
  if (family === 'video') {
    if (TRANSFORMABLE_VIDEO.has(ext) && short && file.size <= MEDIA_TRANSFORM_MAX_BYTES)
      return { kind: 'media', slices: mediaSlices(d as number) };
    return { kind: 'container', op: 'audio' };
  }
  if (family === 'audio') {
    if (DIRECT_AUDIO.has(ext) && short) return { kind: 'original', output: 'audio' };
    return { kind: 'container', op: 'audio' };
  }
  if (file.scan && ext === 'pdf') return { kind: 'container', op: 'pdf' };
  return { kind: 'none' };
}

/** Plain-language names for the card's sub-step and the help article. */
export const MEDIA_SUBSTEP = {
  images: 'Converting the photo',
  media: 'Extracting the sound',
  pdf: 'Turning scanned pages into images',
  image: 'Converting the image pages',
  audio: 'Cutting the sound into segments',
} as const;
